// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { startTransition, useEffect, useState } from 'react';

interface UseDeferredMountResult {
  /**
   * False until the trigger flips true and the next commit lands. Lets the
   * caller render a lightweight skeleton on the same paint that schedules
   * the expensive subtree, then swap to the real content on the next commit
   * without freezing the browser.
   */
  isReady: boolean;
}

/**
 * Defers mounting an expensive subtree until the next commit so the browser
 * can paint a placeholder first. Usage:
 *
 * const { isReady } = useDeferredMount(isEditing);
 * return isReady ? <ExpensiveSubtree /> : <Skeleton />;
 *
 * When `trigger` flips false the ready flag resets, so toggling edit mode on
 * and off re-arms the deferred mount each time.
 */
export function useDeferredMount(trigger: boolean): UseDeferredMountResult {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!trigger) {
      setIsReady(false);
      return;
    }

    // Schedule the ready transition so React can paint the skeleton on the
    // current commit and swap to the real subtree on the next one.
    startTransition(() => {
      setIsReady(true);
    });
  }, [trigger]);

  return { isReady };
}
